const classPath = {
  channel: {
    pathPattern: [],
  },
  channel_admin_role: {
    pathPatternCollection: ["channel_admin_roles"],
    pathPattern: ["channel_admin_roles", null],
  },
  trigger: {
    pathPatternCollection: ["triggers"],
    pathPattern: ["triggers", null],
  },
  trigger_field: {
    pathPatternCollection: ["triggers", null, "trigger_fields"],
    pathPattern: ["triggers", null, "trigger_fields", null],
  },
  ingredient: {
    pathPatternCollection: ["triggers", null, "ingredients"],
    pathPattern: ["triggers", null, "ingredients", null],
  },
  stencil: {
    pathPattern: ["triggers", null, "stencil"],
  },
  stencil_field: {
    pathPatternCollection: ["triggers", null, "stencil", "fields"],
    pathPattern: ["triggers", null, "stencil", "fields", null],
  },
  action: {
    pathPatternCollection: ["actions"],
    pathPattern: ["actions", null],
  },
  action_field: {
    pathPatternCollection: ["actions", null, "action_fields"],
    pathPattern: ["actions", null, "action_fields", null],
  },
}

// Object of Keys by class used to determine identity of objects
// `null` is provided when the class is a grouping class (e.g., Stencil)
// that should not be compared.
const KEY_MATCH = {
  default: "cloned_from_id",
  channel_admin_role: "username",
  stencil: null,
  stencil_field: "id",
  populator_value: "value",
}

// Given a model (e.g., the source or dest model of the diff), retrieve the object at the path
function resolvePath(model, path) {
  if (path.length == 0) return model

  let _path = [...path]
  let key = _path.shift()
  let nextModel

  if (Array.isArray(model)) {
    let cls = model[0] && model[0].class
    if (!cls) {
      throw "Attempt to resolve element in an empty array"
    }
    const keyMatch = KEY_MATCH[cls] || KEY_MATCH["default"]
    nextModel = model.find(element => element[keyMatch] == key)
  } else {
    nextModel = model[key]
  }
  if (!nextModel) {
    throw `Key ${key} not found in model`
  }
  return resolvePath(nextModel, _path)
}

// Determine whether the path matches the specified path pattern
// A path pattern is an array of path parts, where an unknown value (e.g., an element ID) can be denoted as +null+
// For example, the pattern ['triggers', null, 'trigger_field', null] will match ['triggers', 12345, 'trigger_fields', 456]
function pathMatches(path, pathPattern) {
  if (path.length != pathPattern.length) return false
  return path.every((part, i) => !pathPattern[i] || pathPattern[i] == part)
}

// Determine whether the path matches the specified path pattern
// A path pattern is an array of path parts, but optionally an object which contains a cloned_from_id or id can also be
// passed. And either of those values (with cloned_from_id taking preference) can match.
// Additionally, if a hash with __anything: true is passed, it will match on any part.
function prefixPathMatchesWithObject(path, pathPattern) {
  if (path.length < pathPattern.length) return false
  return pathPattern.every((patternPart, i) => {
    if (patternPart !== null && typeof patternPart === "object") {
      if (patternPart.__anything) return true
      let key = KEY_MATCH[patternPart.class] || KEY_MATCH["default"]
      return patternPart[key] == path[i]
    } else {
      return patternPart == path[i]
    }
  })
}

export function pathMatchesWithObject(path, pathPattern) {
  if (path.length != pathPattern.length) return false
  return prefixPathMatchesWithObject(path, pathPattern)
}

export function collectionWasReordered(diffs, path) {
  return !!diffs.find(diff => {
    return diff.event == "reorder" && pathMatchesWithObject(diff.path, path)
  })
}

export function collectionDeletedItems(diffs, original, path) {
  let deleteDiffs = diffs.filter(diff => {
    return (
      diff.event == "delete" &&
      pathMatchesWithObject(diff.path, path.concat([{ __anything: true }]))
    )
  })
  return deleteDiffs.map(diff => {
    return resolvePath(original, diff.path)
  })
}

function changeCollection(diffs, pattern) {
  const created = {},
    updated = {},
    deleted = {}
  const filteredDiffs = diffs.filter(diff =>
    prefixPathMatchesWithObject(diff.path, pattern)
  )
  return filteredDiffs.reduce(
    (acc, diff) => {
      const ref = diff.path[pattern.length]
      switch (diff.event) {
        case "create":
          if (diff.path.length == pattern.length) {
            let key = KEY_MATCH[diff.data.class] || KEY_MATCH["default"]
            acc.created[diff.data[key]] = [diff]
          } else {
            acc.updated[ref] = acc.updated[ref]
              ? acc.updated[ref].concat([diff])
              : [diff]
          }
          break
        case "delete":
          if (diff.path.length == pattern.length + 1) {
            acc.deleted[ref] = [diff]
          } else {
            acc.updated[ref] = acc.updated[ref]
              ? acc.updated[ref].concat([diff])
              : [diff]
          }
          break
        case "update":
          acc.updated[ref] = acc.updated[ref]
            ? acc.updated[ref].concat([diff])
            : [diff]
          break
      }
      return acc
    },
    { created, updated, deleted }
  )
}

export function setupCollectionForRender(diffs, collection, pattern) {
  const changes = changeCollection(diffs, pattern)
  return collection.map(item => {
    let key = KEY_MATCH[item.class] || KEY_MATCH["default"]
    let identifier = item[key]
    if (changes.created[identifier]) {
      return { ...item, type: "new", changes: changes.created[identifier] }
    } else if (changes.updated[identifier]) {
      return { ...item, type: "updated", changes: changes.updated[identifier] }
    } else if (changes.deleted[identifier]) {
      return { ...item, type: "deleted", changes: changes.deleted[identifier] }
    } else {
      return { ...item, type: "stale", changes: [] }
    }
  })
}
